import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { alpha } from '@mui/material/styles';
import client from '../API';

const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { 
  month: 'short', day: 'numeric', year: 'numeric' 
}) : '—';

/**
 * Simplified CommissionsSection - Shows claimed commission history
 * When a commission is claimed, it's immediately added to balance (paid)
 */
export default function CommissionsSection() {
  // Fetch claimed commissions
  const { data: claimedData, isLoading, refetch } = useQuery(
    'claimed-commissions',
    () => client.commissions.getClaimedCommissions(),
    { 
      staleTime: 1000 * 30,
      onSuccess: (data) => {
        console.log('✅ Commission data:', {
          claimed: data?.data?.claimed_commissions || data?.claimed_commissions || [],
          total: data?.data?.total_claimed_amount || data?.total_claimed_amount || 0
        });
      }
    }
  );

  // Fetch commission summary
  const { data: summaryData, refetch: refetchSummary } = useQuery(
    'commission-summary',
    () => client.commissions.getCommissionSummary(),
    { staleTime: 1000 * 30 }
  );

  // Extract data
  const allCommissions = claimedData?.data?.claimed_commissions 
    || claimedData?.claimed_commissions 
    || [];
  
  const totalEarnings = claimedData?.data?.total_claimed_amount 
    || claimedData?.total_claimed_amount 
    || 0;

  const handleRefresh = () => {
    refetch();
    refetchSummary();
  };

  return (
    <Box>
      {/* Hero Stats */}
      <Grid container spacing={3} mb={4}>
        {/* Total Earnings Card */}
        <Grid item xs={12}>
          <Card 
            elevation={0}
            sx={{ 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              minHeight: 180,
            }}
          >
            <CardContent sx={{ py: 4 }}>
              <Stack direction="row" spacing={3} alignItems="center">
                <Box sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 3,
                  bgcolor: alpha('#fff', 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <WalletIcon sx={{ fontSize: 40 }} />
                </Box>
                <Box flex={1}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.9, 
                      fontWeight: 600, 
                      textTransform: 'uppercase', 
                      fontSize: '0.8rem',
                      mb: 1
                    }}
                  >
                    Total Commission Earnings
                  </Typography>
                  <Typography variant="h2" fontWeight={900} sx={{ mb: 1 }}>
                    {fmtMoney(totalEarnings)}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 600 }}>
                      {allCommissions.length} commissions claimed
                    </Typography>
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="All paid to balance"
                      sx={{ 
                        bgcolor: alpha('#fff', 0.2),
                        color: 'white',
                        fontWeight: 700
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12} sm={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              textAlign: 'center',
              border: `2px solid ${alpha('#10b981', 0.2)}`,
              borderRadius: 2,
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              Average Commission
            </Typography>
            <Typography variant="h5" fontWeight={800} color="success.main">
              {allCommissions.length > 0 
                ? fmtMoney(totalEarnings / allCommissions.length)
                : '$0.00'
              }
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              textAlign: 'center',
              border: `2px solid ${alpha('#6366f1', 0.2)}`,
              borderRadius: 2,
            }}
          >
            <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              Latest Commission
            </Typography>
            <Typography variant="h5" fontWeight={800} color="primary.main">
              {allCommissions.length > 0 
                ? fmtMoney(allCommissions[0].commission_amount)
                : '$0.00'
              }
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              textAlign: 'center',
              border: `2px solid ${alpha('#f59e0b', 0.2)}`,
              borderRadius: 2,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 32, color: '#d97706', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              Last Claimed
            </Typography>
            <Typography variant="body1" fontWeight={700} sx={{ color: '#d97706' }}>
              {allCommissions.length > 0 
                ? fmtDate(allCommissions[0].claimed_at)
                : '—'
              }
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Commission History */}
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ 
          p: 3,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha('#000', 0.02)
        }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Commission History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All claimed commissions are immediately added to your balance
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ p: 3 }}>
          {isLoading ? (
            <Box py={4}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography textAlign="center" color="text.secondary">
                Loading commission history...
              </Typography>
            </Box>
          ) : allCommissions.length === 0 ? (
            <Box textAlign="center" py={8}>
              <WalletIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2, opacity: 0.3 }} />
              <Typography variant="h5" color="text.secondary" fontWeight={700} gutterBottom>
                No Commissions Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Claimed commissions will appear here
              </Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Date Claimed
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Source
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Referral
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Source Amount
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Rate
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Commission
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allCommissions.map((commission) => (
                    <TableRow 
                      key={commission.id}
                      sx={{ 
                        '&:hover': {
                          bgcolor: alpha('#10b981', 0.05)
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {fmtDate(commission.claimed_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(commission.claimed_at).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip 
                            size="small"
                            label={commission.source_type || 'N/A'}
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: '0.7rem',
                              textTransform: 'capitalize'
                            }}
                          />
                          {commission.source_id && (
                            <Typography variant="caption" color="text.secondary">
                              #{commission.source_id}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip 
                          size="small"
                          label={commission.commission_type || 'general'}
                          color={
                            commission.commission_type === 'blitzpay' ? 'primary' :
                            commission.commission_type === 'project' ? 'secondary' :
                            'default'
                          }
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.7rem',
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        {commission.referral_id ? (
                          <Typography variant="body2" fontWeight={600}>
                            Referral #{commission.referral_id}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {commission.source_amount 
                            ? fmtMoney(commission.source_amount)
                            : '—'
                          }
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          {commission.commission_rate 
                            ? `${(parseFloat(commission.commission_rate) * 100).toFixed(2)}%`
                            : '—'
                          }
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="h6" fontWeight={800} color="success.main">
                          {fmtMoney(commission.commission_amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}